import { Injectable, inject } from '@angular/core';
import { geoJSON, GeoJSON, LatLng, Layer, PathOptions } from 'leaflet';
import * as L from 'leaflet';
import { LeafletMapboxService } from '../leaflet-mapbox.service';
import { ScaleService } from '../scale.service';
import { DataEntry } from '../../models/indicator-data';
import { Scale } from '../../models/scale';
import { Feature, PoiMarkerOptions } from '../../models/geo-data';
import { Indicator } from '../../models/indicator';
import { isNumber } from 'chart.js/helpers';
import { DataLayerTypeIcon } from '../../stores/data-layer-type/data-layer-type.selectors';
import { generateHarmoniousColors } from '../../utility/color.utility';
import { PoiConf } from '../grid-widget-config.service';

const defaultStyle: PathOptions = {
  color: '#505050',
  weight: 1,
  opacity: 1,
  fillOpacity: 0,
};

@Injectable({
  providedIn: 'root',
})
export class MapRenderService {
  private readonly mapboxService = inject(LeafletMapboxService);
  private readonly scaleService = inject(ScaleService);

  /**
   * Creates a styled marker based on the provided feature and options. Depending on the input,
   * the method either generates a ring chart marker for multiple indicators or a styled marker
   * for a single indicator.
   *
   * @param {any} feature The feature object containing properties for marker creation.
   * @param {PoiMarkerOptions} options An object containing configuration for the marker, including data, scales, icons, and positioning.
   * @param {string[]} colors An array of color strings used for styling the marker.
   * @return {Layer|undefined} Returns a styled marker layer or a ring chart marker layer. Returns undefined if the options or icon are invalid.
   */
  createStyledMarker(
    feature: any,
    options: PoiMarkerOptions,
    colors: string[]
  ): Layer | undefined {
    if (!options || !options.icon) return;

    const key = feature.properties?.key;

    // Check if we have multiple data entries (multiple indicators)
    if (
      Array.isArray(options.data) &&
      options.data.length > 1 &&
      Array.isArray(options.scales) &&
      options.indicators
    ) {
      // Multiple indicators: Create ring chart
      return this.createRingChartMarker(feature, options, key, colors);
    } else {
      // Single indicator: Use existing styled marker logic
      let color = '#FFFFFF';
      const singleData = Array.isArray(options.data)
        ? options.data[0]
        : options.data;
      const singleScales =
        Array.isArray(options.scales) && Array.isArray(options.scales[0])
          ? options.scales[0]
          : options.scales;

      if (singleScales && singleData) {
        const value = isNumber(singleData[key]) ? singleData[key] : null;
        color =
          value !== null
            ? this.scaleService.getColor(value, singleScales as Scale[], colors)
            : '#efefef';
      }
      return this.mapboxService.createMarker(
        options.latlng,
        options.icon?.svg,
        color,
        options.icon?.color || '#000000',
        options.size
      );
    }
  }

  /**
   * Creates a ring chart marker for a given map feature.
   *
   * @param {any} feature The map feature associated with the marker.
   * @param {PoiMarkerOptions} options Configuration options for creating the marker,
   *                                   including size, icon data, and indicators.
   * @param {string} key The key used to extract values from the data entries.
   * @param {string[]} colors An array of colors used to represent different indicators.
   * @return {Layer} A Leaflet layer representing the ring chart marker.
   */
  private createRingChartMarker(
    feature: any,
    options: PoiMarkerOptions,
    key: string,
    colors: string[]
  ): Layer {
    const dataArray = options.data as DataEntry[];
    const indicators = options.indicators || [];
    const size = options.size || 1;
    const chartSize = size * 30;
    const icon = options.icon;

    if (!icon) {
      throw new Error('Icon is required for ring chart marker');
    }

    // Generate fixed colors for each indicator
    const indicatorColors = this.getIndicatorColors(indicators, colors);

    // Collect values and colors for each indicator
    const chartData: {
      value: number;
      color: string;
      indicator: Indicator;
    }[] = [];

    dataArray.forEach((data, index) => {
      if (data && indicators[index]) {
        const value = isNumber(data[key]) ? data[key] : null;
        if (value !== null) {
          const color = indicatorColors[index];
          chartData.push({ value, color, indicator: indicators[index] });
        }
      }
    });

    if (chartData.length === 0) {
      // Fallback to simple marker if no data
      return this.mapboxService.createMarker(
        options.latlng,
        icon?.svg,
        '#efefef',
        icon?.color || '#000000',
        options.size
      ) as Layer;
    }

    const pathsHTML = this.mapboxService.pieChartSvgFromData(
      chartData,
      chartSize
    );
    const iconColor = icon.color || '#000000';
    const svgHtml = icon.svg || '';

    const chartHtml = `
      <svg class="ring-chart" width="${chartSize}" height="${chartSize}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${chartSize} ${chartSize}">
        ${pathsHTML}
      </svg>
    `;
    const svg = this.mapboxService.getSanitizedSvg(svgHtml);
    const iconHtml = `<div class="poi-icon" style="fill:${iconColor};">${svg}</div>`;

    const html = `<div class="" style="transform: scale(${size});">${iconHtml}${chartHtml}</div>`;

    const divIcon = L.divIcon({
      className: '',
      html: html,
      iconSize: [40 * size, 40 * size],
      iconAnchor: [20 * size, 40 * size],
    });

    return L.marker(options.latlng, { icon: divIcon });
  }

  /**
   * Determines a set of colors for the given indicators, ensuring each indicator is assigned a color.
   * If the number of indicators exceeds the number of provided colors, additional colors are generated harmoniously.
   *
   * @param {Indicator[]} indicators - The list of indicators that need colors assigned.
   * @param {string[]} colors - The available base colors for assignment.
   * @return {string[]} An array of colors corresponding to the indicators.
   */
  getIndicatorColors(indicators: Indicator[], colors: string[]): string[] {
    if (indicators.length <= colors.length) {
      return indicators.map((_, index) => colors[index % colors.length]);
    } else {
      return generateHarmoniousColors(colors);
    }
  }

  /**
   * Retrieves the colors for Points of Interest (POI) based on the given configuration.
   *
   * @param {PoiConf} poiConfig - The configuration object for the POI, which may include legend settings.
   * @param {string[]} defaultColors - The array of default colors to be used if no specific configuration is provided.
   * @return {string[]} An array of colors, potentially reversed based on the provided configuration.
   */
  getPoiColors(poiConfig: PoiConf, defaultColors: string[]): string[] {
    const colors = defaultColors;
    const copy = [...colors];
    if (poiConfig && poiConfig.legend && poiConfig.legend.invert) {
      copy.reverse();
    }
    return copy;
  }

  /**
   * Generates a style configuration object for a geographic feature based on input data, scales, and other parameters.
   *
   * @param {any} feature - The geographic feature for which the style is being generated. Typically contains properties like `id` or `key`.
   * @param {DataEntry | undefined} data - The data object containing values for features, used to determine the feature's fill color.
   * @param {Scale[] | undefined} scales - An array of scales used to map data values to colors.
   * @param {string[]} colors - A list of colors used for the color scale.
   * @param {string | null | undefined} [highlightedFeatureKey] - The key of the feature to highlight. If it matches the current feature, a specific style is applied.
   * @param {string[] | undefined} [selectedFeatureKeys] - A list of keys representing selected features. If the current feature's key is in this list, a specific style is applied.
   * @param {number | undefined} [districtOpacity] - The opacity level for the feature's fill color.
   * @return {PathOptions} A style configuration object used for rendering the feature. Contains properties such as `color`, `fillColor`, `dashArray`, `weight`, and `fillOpacity`.
   */
  styleFn(
    feature: any,
    data: DataEntry | undefined,
    scales: Scale[] | undefined,
    colors: string[],
    highlightedFeatureKey?: string | null,
    selectedFeatureKeys?: string[],
    districtOpacity?: number
  ): PathOptions {
    if (data && scales && feature) {
      const key = feature.properties?.key || feature.id;
      const value = data[key] ?? null;
      const color =
        value !== null
          ? this.scaleService.getColor(value, scales, colors)
          : '#efefef';

      if (highlightedFeatureKey === String(key)) {
        return {
          ...defaultStyle,
          color: '#222222',
          fillColor: color,
          dashArray: [2, 6],
          weight: 3,
          fillOpacity: districtOpacity || 0.7,
        };
      }

      if (selectedFeatureKeys?.some(item => item === String(key))) {
        return {
          ...defaultStyle,
          color: '#444444',
          fillColor: color,
          dashArray: undefined,
          weight: 3,
          fillOpacity: districtOpacity || 0.7,
        };
      }

      return {
        ...defaultStyle,
        color: color,
        dashArray: undefined,
        weight: 2,
        fillOpacity: districtOpacity || 0.7,
      };
    } else {
      return defaultStyle;
    }
  }

  /**
   * Creates a point layer on the map by rendering GeoJSON data using the provided configuration and styling options.
   *
   * @param {PoiConf} poiConfig - The configuration object for the point of interest (POI) layer.
   * @param {DataEntry[] | undefined} data - An array of data entries associated with the points or undefined if no data is provided.
   * @param {GeoJSON.FeatureCollection} geoJsonData - The GeoJSON feature collection representing the points to be rendered.
   * @param {Scale[][] | undefined} scales - A 2D array of scale objects to be used for styling the points, or undefined if no scales are provided.
   * @param {DataLayerTypeIcon} icon - The icon to be used for the points in the layer.
   * @param {Indicator[] | undefined} indicators - An optional array of indicators associated with the points or undefined if no indicators are used.
   * @param {string[]} colors - An array of colors utilized for the styling of the points.
   * @param {number} poiSize - The size of the POI icons to be drawn on the layer.
   * @param {(feature: any, layer: Layer) => void} [onEachFeature] - An optional callback function that will be executed for each feature in the GeoJSON data.
   * @return {GeoJSON} The generated GeoJSON object representing the point layer.
   */
  createPointLayer(
    poiConfig: PoiConf,
    data: DataEntry[] | undefined,
    geoJsonData: GeoJSON.FeatureCollection,
    scales: Scale[][] | undefined,
    icon: DataLayerTypeIcon,
    indicators: Indicator[] | undefined,
    colors: string[],
    poiSize: number,
    onEachFeature?: (feature: any, layer: Layer) => void
  ): GeoJSON {
    return geoJSON(geoJsonData, {
      style: feature => this.styleFn(feature, data?.[0], scales?.[0], colors),
      pointToLayer: (feature: Feature, latlng: LatLng) => {
        return this.createStyledMarker(
          feature,
          {
            latlng,
            data,
            scales,
            colors,
            icon,
            size: poiSize || 1,
            indicators,
          },
          colors
        ) as Layer;
      },
      onEachFeature: onEachFeature || undefined,
    });
  }
}
